import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import AppIcon from "../../../components/ui/AppIcon";

/**
 * DraftIndicator - Shows draft status with subtle animation
 * Displays "Draft saved" or "Saving..." at bottom of input area
 */
export default function DraftIndicator({ lastSaved, hasUnsavedChanges, isLoading }) {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState("");
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setMessage("Saving draft...");
      setVisible(true);
      setFadeOut(false);
    } else if (lastSaved) {
      setMessage("Draft saved");
      setVisible(true);
      setFadeOut(false);

      // Auto-hide after 2 seconds
      const timer = setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => setVisible(false), 300);
      }, 2000);

      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [lastSaved, hasUnsavedChanges, isLoading]);

  if (!visible) return null;

  return (
    <div
      className={`absolute bottom-16 right-4 flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all duration-300 ${hasUnsavedChanges
          ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
          : isLoading
            ? "bg-blue-500/10 text-blue-400 border border-blue-500/20 animate-pulse"
            : fadeOut
              ? "bg-green-500/10 text-green-400/0 border border-green-500/0 opacity-0"
              : "bg-green-500/10 text-green-400 border border-green-500/20 animate-fade-in"
        }`}
    >
      {hasUnsavedChanges && (
        <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
      )}
      {isLoading && !hasUnsavedChanges && (
        <div className="w-2 h-2 rounded-full bg-blue-400 animate-spin" />
      )}
      {!hasUnsavedChanges && !isLoading && !fadeOut && (
        <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
      <span>{message}</span>
    </div>
  );
}

DraftIndicator.propTypes = {
  lastSaved: PropTypes.instanceOf(Date),
  hasUnsavedChanges: PropTypes.bool,
  isLoading: PropTypes.bool,
};
